import {
  Bars3BottomLeftIcon,
  CalendarIcon,
  ChatBubbleLeftRightIcon,
  ChevronUpDownIcon,
  DocumentTextIcon,
  EllipsisHorizontalIcon,
  PaperClipIcon,
  TrashIcon,
  ViewColumnsIcon,
} from "@heroicons/react/20/solid";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { Discussion } from "@/components/collab/Discussion";
import { EntityFiles } from "@/components/files/Files";
import { Breadcrumbs, EditableTitle, EntityBody, Tabs } from "@/components/page";
import { AddPropertyButton, CustomPropertyRows, DateEditor, PropertyRow, ValueButton } from "@/components/properties/Properties";
import { ChoicePopover } from "@/components/tasks/pickers";
import { TaskViews } from "@/components/tasks/TaskViews";
import { ErrorState, PageSpinner, ProgressBar, Tag } from "@/components/ui";
import { useFeedback } from "@/components/ui/feedback";
import { MenuItem, Popover } from "@/components/ui/Popover";
import { api, errorMessage, isStatus } from "@/lib/api";
import { useProject } from "@/lib/project";
import { keys, useSprint, useSprints } from "@/lib/queries";
import type { Sprint } from "@/lib/types";
import { formatDay, parseDay } from "@/lib/utils";

type SprintPatch = Parameters<typeof api.updateSprint>[2];

function useSprintUpdate(sprint: Sprint) {
  const { pid } = useProject();
  const qc = useQueryClient();
  return async (patch: SprintPatch) => {
    qc.setQueryData<Sprint>(keys.sprint(pid, sprint.id), (s) => (s ? { ...s, ...patch } as Sprint : s));
    try {
      await api.updateSprint(pid, sprint.id, patch);
    } catch (err) {
      window.dispatchEvent(new CustomEvent("app:error", { detail: err }));
    }
    qc.invalidateQueries({ queryKey: keys.project(pid) });
  };
}

function GoalField({ sprint }: { sprint: Sprint }) {
  const { canEdit } = useProject();
  const update = useSprintUpdate(sprint);
  const [draft, setDraft] = useState(sprint.goal);
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setDraft(sprint.goal);
  }, [sprint.goal, focused]);
  return (
    <textarea
      value={draft}
      readOnly={!canEdit}
      rows={Math.max(1, Math.ceil(draft.length / 80))}
      placeholder="Empty"
      dir="auto"
      onFocus={() => setFocused(true)}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        setFocused(false);
        if (draft.trim() !== sprint.goal) update({ goal: draft.trim() });
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          (e.target as HTMLTextAreaElement).blur();
        }
      }}
      className="block min-h-[34px] w-full resize-none rounded-md bg-transparent px-2 py-[7px] text-sm outline-none placeholder:text-faint hover:bg-hover focus:bg-hover"
    />
  );
}

function SprintFields({ sprint }: { sprint: Sprint }) {
  const { bundle, canEdit, sprintStatusById } = useProject();
  const update = useSprintUpdate(sprint);
  const status = sprint.statusId ? sprintStatusById.get(sprint.statusId) : undefined;
  const days = sprint.startDate && sprint.endDate ? Math.round((parseDay(sprint.endDate).getTime() - parseDay(sprint.startDate).getTime()) / 86400000) + 1 : null;

  const dateButton = (field: "startDate" | "endDate", placeholder: string) => (
    <Popover
      disabled={!canEdit}
      trigger={(t) => (
        <button ref={t.ref} onClick={t.onClick} type="button" disabled={!canEdit} className="rounded px-1 py-0.5 hover:bg-hover disabled:hover:bg-transparent">
          {sprint[field] ? formatDay(sprint[field]) : <span className="text-faint">{placeholder}</span>}
        </button>
      )}
    >
      {(close) => <DateEditor value={sprint[field] ?? undefined} onChange={(v) => update({ [field]: v })} onClose={close} />}
    </Popover>
  );

  return (
    <div className="space-y-px">
      <PropertyRow icon={Bars3BottomLeftIcon} label="Sprint Goal">
        <GoalField sprint={sprint} />
      </PropertyRow>
      <PropertyRow icon={ChevronUpDownIcon} label="Status">
        <ChoicePopover
          value={sprint.statusId}
          disabled={!canEdit}
          title="Sprint status"
          choices={[{ id: null, label: "No status" }, ...bundle.sprintStatuses.map((s) => ({ id: s.id, label: s.name, color: s.color }))]}
          onChange={(statusId) => update({ statusId })}
          trigger={(t) => (
            <ValueButton ref={t.ref} onClick={t.onClick} empty={!status} disabled={!canEdit}>
              {status && <Tag color={status.color}>{status.name}</Tag>}
            </ValueButton>
          )}
        />
      </PropertyRow>
      <PropertyRow icon={CalendarIcon} label="Timeline">
        <div className="flex min-h-[34px] flex-wrap items-center gap-1 px-1 text-sm">
          {dateButton("startDate", "Start date")}
          <span className="text-faint">→</span>
          {dateButton("endDate", "End date")}
          {days !== null && <span className="ms-2 text-xs text-faint">{days} days</span>}
        </div>
      </PropertyRow>
      <CustomPropertyRows entityType="sprint" entityId={sprint.id} values={sprint.values} />
      <AddPropertyButton entityType="sprint" />
    </div>
  );
}

type SprintTab = "tasks" | "specification" | "files" | "discussion";

export function SprintPage() {
  const { sid = "" } = useParams();
  const { pid, bundle, canEdit } = useProject();
  const sprint = useSprint(pid, sid);
  const siblings = useSprints(pid, sprint.data?.epicId);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { confirm } = useFeedback();
  const [params, setParams] = useSearchParams();
  const tab = (params.get("tab") as SprintTab) || "tasks";
  const setTab = (t: SprintTab) => {
    const next = new URLSearchParams(params);
    next.set("tab", t);
    setParams(next, { replace: true });
  };

  if (sprint.isPending) return <PageSpinner />;
  if (sprint.error) return <ErrorState message={isStatus(sprint.error, 404) ? "This sprint was deleted or doesn't exist." : errorMessage(sprint.error)} />;
  const s = sprint.data;
  const stats = siblings.data?.find((x) => x.id === s.id);

  return (
    <>
      <Breadcrumbs
        items={[
          { label: bundle.project.name, to: `/p/${pid}` },
          { label: s.epicTitle, to: `/p/${pid}/epics/${s.epicId}?tab=sprints` },
          { label: s.title },
        ]}
        right={
          canEdit && (
            <Popover
              placement="bottom-end"
              className="w-48 p-1"
              trigger={(t) => (
                <button ref={t.ref} onClick={t.onClick} className="rounded p-1.5 text-muted hover:bg-hover" aria-label="More">
                  <EllipsisHorizontalIcon className="size-5" />
                </button>
              )}
            >
              {(close) => (
                <MenuItem
                  icon={TrashIcon}
                  danger
                  onClick={async () => {
                    close();
                    const ok = await confirm({
                      title: `Delete "${s.title}"?`,
                      message: "The sprint will be deleted. Its tasks are kept and moved back to the epic's backlog.",
                      confirmLabel: "Delete sprint",
                      danger: true,
                    });
                    if (!ok) return;
                    try {
                      await api.deleteSprint(pid, s.id);
                      await qc.invalidateQueries({ queryKey: keys.project(pid) });
                      navigate(`/p/${pid}/epics/${s.epicId}?tab=sprints`);
                    } catch (err) {
                      window.dispatchEvent(new CustomEvent("app:error", { detail: err }));
                    }
                  }}
                >
                  Delete sprint
                </MenuItem>
              )}
            </Popover>
          )
        }
      />
      <div className="px-6 pt-8 md:px-12">
        <div className="max-w-4xl">
          <EditableTitle value={s.title} editable={canEdit} onSave={(title) => saveSprint(qc, pid, s, { title })} className="mb-4" />
          <SprintFields sprint={s} />
          {stats && stats.taskCount > 0 && <ProgressBar done={stats.doneCount} total={stats.taskCount} className="mt-3 max-w-md ps-1.5" />}
        </div>
        <div className="mt-6 max-w-4xl">
          <Tabs
            value={tab}
            onChange={setTab}
            tabs={[
              { id: "tasks", label: `Tasks${stats ? ` (${stats.taskCount})` : ""}`, icon: ViewColumnsIcon },
              { id: "specification", label: "Specification", icon: DocumentTextIcon },
              { id: "files", label: "Files", icon: PaperClipIcon },
              { id: "discussion", label: "Discussion", icon: ChatBubbleLeftRightIcon },
            ]}
          />
        </div>
      </div>
      <div className="pt-5 pb-24">
        {tab === "tasks" && <TaskViews scope={{ sprintId: s.id }} storageKey={`view:${pid}:sprint`} createIn={{ epicId: s.epicId, sprintId: s.id }} />}
        {tab === "specification" && (
          <div className="max-w-4xl px-6 pt-4 md:px-12">
            <EntityBody entityId={s.id} title={s.title} initial={s.body} save={(body) => api.updateSprint(pid, s.id, { body })} />
          </div>
        )}
        {tab === "files" && (
          <div className="max-w-4xl px-6 md:px-12">
            <EntityFiles entityType="sprint" entityId={s.id} title={s.title} />
          </div>
        )}
        {tab === "discussion" && (
          <div className="max-w-4xl px-6 md:px-12">
            <Discussion entityId={s.id} />
          </div>
        )}
      </div>
    </>
  );
}

async function saveSprint(qc: ReturnType<typeof useQueryClient>, pid: string, sprint: Sprint, patch: SprintPatch) {
  try {
    await api.updateSprint(pid, sprint.id, patch);
  } catch (err) {
    window.dispatchEvent(new CustomEvent("app:error", { detail: err }));
  }
  qc.invalidateQueries({ queryKey: keys.project(pid) });
}
